import { BaseAPI } from 'API/BaseApi';
import { eAPITags } from 'API/eAPITags';
import { ICreateCheckDisbursementRequest } from 'types/order/escrow/checks/ICreateCheckDisbursementRequest';
import { IUpdateCheckDisbursementRequest } from 'types/order/escrow/checks/IUpdateCheckDisbursementRequest';
import { IManualCheckDisbursementVoidRequest } from '../../../types/accounting/IManualCheckDisbursementVoidRequest';
import { emptyFieldsToUndefined } from './EscrowBaseAPI';
import { ICheckDisbursementFormData } from 'types/order/escrow/checks/ICheckDisbursementFormData';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';
import { IManualCheckDisbursementResponse } from 'types/order/escrow/response/IManualCheckDisbursementResponse';

export interface ICreateCheckDisbursementProps {
  escrowFileId: string;
  orderId: string;
  check: ICreateCheckDisbursementRequest;
}

export interface IUpdateCheckDisbursementProps {
  orderId: string;
  check: IUpdateCheckDisbursementRequest;
}

const CheckDisbursementAPI = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    getCheckDisbursement: build.query<ICheckDisbursementFormData, { checkId: string }>({
      query: ({ checkId }) => ({
        url: `/timber/api/internal/escrow/escrow_manual_check_disbursements/${checkId}?includes=ADDRESS_INCLUDE`,
        method: 'GET',
      }),
      transformResponse: (check: IManualCheckDisbursementResponse) => ({
        ...check,
        mailingAddress: check.mailingAddress ?? {
          stateAbbreviation: 'AL',
          streetAddress: 'Unknown',
          city: 'Unknown',
          zipCode: '12345',
        },
        type: eDisbursementType.Check,
      }),
      providesTags: (result, error, { checkId }) => [{ type: eAPITags.CheckDisbursement, id: checkId }],
    }),
    createCheckDisbursement: build.mutation<void, ICreateCheckDisbursementProps>({
      query: ({ escrowFileId, check }) => ({
        url: `/timber/api/internal/escrow/${escrowFileId}/escrow_manual_check_disbursements`,
        method: 'POST',
        body: emptyFieldsToUndefined(check),
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
      ],
    }),
    updateCheckDisbursement: build.mutation<void, IUpdateCheckDisbursementProps>({
      query: ({ check }) => ({
        url: `/timber/api/internal/escrow/escrow_manual_check_disbursements/${check.id}`,
        method: 'PUT',
        body: emptyFieldsToUndefined(check),
      }),
      invalidatesTags: (result, error, { orderId, check }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.CheckDisbursement, id: check.id },
      ],
    }),
    voidCheckDisbursement: build.mutation<void, { orderId: string; check: IManualCheckDisbursementVoidRequest }>({
      query: ({ check }) => ({
        url: `/timber/api/internal/escrow/escrow_manual_check_disbursements/void`,
        method: 'POST',
        body: check,
      }),
      invalidatesTags: (result, error, { orderId, check }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.CheckDisbursement, id: check.id },
      ],
    }),
    deleteCheckDisbursement: build.mutation<void, { checkId: string; orderId: string }>({
      query: ({ checkId }) => ({
        url: `/timber/api/internal/escrow/escrow_manual_check_disbursements/${checkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId, checkId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.CheckDisbursement, id: checkId },
      ],
    }),
    printAndSetCheckDate: build.mutation<Uint8Array, { checkId: string }>({
      query: ({ checkId }) => ({
        url: `/timber/api/internal/escrow/escrow_manual_check_disbursements/${checkId}/print_manual_check_and_set_check_date`,
        method: 'POST',
        responseHandler: (response: { blob: () => any }) => response.blob(),
      }),
    }),
    printAndSendDisbursement: build.mutation<void, { checkId: string; orderId: string }>({
      query: ({ checkId }) => ({
        url: `/timber/api/internal/escrow/escrow_manual_check_disbursements/${checkId}/print_and_send_manual_check`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { orderId, checkId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.CheckDisbursement, id: checkId },
      ],
    }),
    printCheck: build.mutation<void, { checkId: string; orderId: string }>({
      query: ({ checkId }) => ({
        url: `/timber/api/internal/escrow/escrow_manual_check_disbursements/${checkId}/print_manual_check`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { orderId, checkId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.CheckDisbursement, id: checkId },
      ],
    }),
    updateTracking: build.mutation<
      void,
      { checkId: string; trackingNumber?: string; shipmentType?: string; orderId: string }
    >({
      query: ({ checkId, trackingNumber, shipmentType }) => ({
        url: `/timber/api/internal/escrow/escrow_manual_check_disbursements/${checkId}/tracking`,
        method: 'PATCH',
        body: { trackingNumber, shipmentType },
      }),
      invalidatesTags: (result, error, { orderId, checkId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.CheckDisbursement, id: checkId },
      ],
    }),
  }),
});

export const {
  useGetCheckDisbursementQuery,
  useCreateCheckDisbursementMutation,
  useUpdateCheckDisbursementMutation,
  useVoidCheckDisbursementMutation,
  useDeleteCheckDisbursementMutation,
  usePrintAndSetCheckDateMutation,
  usePrintAndSendDisbursementMutation,
  usePrintCheckMutation,
  useUpdateTrackingMutation,
} = CheckDisbursementAPI;
